import React, { useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, CircularProgress, Chip, IconButton, Tooltip } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { engCampaignAnalyticsAction, engResetMutation } from "../../containers/engagement/action";
import theme from "../../theme";
import { tokens, toMessages, ERROR_FALLBACKS } from "./utils";

const useStyles = makeStyles(() => ({
  back: { color: theme.colors.DodgerBlue, fontFamily: theme.fontFamily.MARKPROMEDIUM },
  title: { fontSize: 18, fontFamily: theme.fontFamily.MARKPROBOLD, color: theme.colors.DodgerBlue, marginBottom: 20 },
  statsRow: { display: "flex", gap: 16, marginBottom: 28, flexWrap: "wrap" },
  statCard: {
    flex: "1 1 140px",
    background: tokens.surface,
    border: `1px solid ${tokens.border}`,
    borderRadius: 10,
    padding: "16px 20px",
    textAlign: "center",
  },
  statValue: { fontSize: 28, fontFamily: theme.fontFamily.MARKPROBOLD, color: theme.colors.DodgerBlue },
  statLabel: { fontSize: 12, color: tokens.textMuted, marginTop: 4, fontFamily: theme.fontFamily.MARKPRO },
  variantTable: {
    width: "100%",
    borderCollapse: "collapse",
    background: tokens.surface,
    border: `1px solid ${tokens.border}`,
    borderRadius: 10,
    overflow: "hidden",
  },
  th: {
    padding: "10px 16px",
    fontSize: 12,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
    background: tokens.tint,
    textAlign: "left",
    borderBottom: `1px solid ${tokens.border}`,
  },
  td: {
    padding: "10px 16px",
    fontSize: 13,
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPRO,
    borderBottom: `1px solid ${tokens.borderSoft}`,
  },
  sectionTitle: { fontSize: 15, fontFamily: theme.fontFamily.MARKPROBOLD, color: theme.colors.DodgerBlue, margin: "0 0 12px" },
  empty: { color: tokens.textSubtle, fontSize: 13, fontFamily: theme.fontFamily.MARKPRO },
  variantChip: { backgroundColor: theme.colors.DodgerBlue, color: theme.colors.White, fontFamily: theme.fontFamily.MARKPROMEDIUM },
  toolbar: { display: "flex", alignItems: "center", gap: 8, marginBottom: 20 },
}));

const pct = (v) => (v !== undefined && v !== null) ? `${Number(v).toFixed(1)}%` : "—";
const num = (v) => (v !== undefined && v !== null) ? Number(v).toLocaleString() : "—";

export default function CampaignAnalytics({ campaignId, onClose }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { analytics, analyticsLoading, mutationError, lastMutation } = useSelector((s) => s.engagementReducer);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      dispatch(engCampaignAnalyticsAction(campaignId));
    }, 15000);
    return () => clearInterval(intervalRef.current);
  }, [campaignId, dispatch]);

  useEffect(() => {
    if (mutationError && lastMutation === "campaign_analytics") {
      toMessages(mutationError, ERROR_FALLBACKS.campaign_analytics).forEach((m) =>
        enqueueSnackbar(m, { variant: "error" })
      );
      dispatch(engResetMutation());
    }
  }, [mutationError, lastMutation, dispatch, enqueueSnackbar]);

  if (analyticsLoading && !analytics) {
    return <CircularProgress size={28} style={{ color: theme.colors.DodgerBlue, margin: "40px auto", display: "block" }} />;
  }

  const overall = analytics;
  const variants = analytics?.variants || [];

  return (
    <div>
      <div className={classes.toolbar}>
        <Button className={classes.back} style={{ margin: 0 }} startIcon={<ArrowBackIcon />} onClick={onClose}>
          Back to Campaigns
        </Button>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => dispatch(engCampaignAnalyticsAction(campaignId))} disabled={analyticsLoading}>
            <RefreshIcon fontSize="small" style={{ color: analyticsLoading ? tokens.textSubtle : theme.colors.DodgerBlue }} />
          </IconButton>
        </Tooltip>
      </div>

      {!analytics ? (
        <div className={classes.empty}>No analytics data yet for this campaign.</div>
      ) : (
        <>
          <div className={classes.title}>Campaign Analytics</div>

          <div className={classes.statsRow}>
            <div className={classes.statCard}>
              <div className={classes.statValue}>{num(overall?.sent_count)}</div>
              <div className={classes.statLabel}>Sent</div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statValue}>{pct(overall?.delivered_pct)}</div>
              <div className={classes.statLabel}>Delivered</div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statValue}>{pct(overall?.opened_pct)}</div>
              <div className={classes.statLabel}>Opened</div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statValue}>{pct(overall?.clicked_pct)}</div>
              <div className={classes.statLabel}>Clicked (CTR)</div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statValue}>{pct(overall?.opted_out_pct)}</div>
              <div className={classes.statLabel}>Opted Out</div>
            </div>
          </div>

          {variants.length > 0 && (
            <>
              <p className={classes.sectionTitle}>A/B Variant Breakdown</p>
              <table className={classes.variantTable}>
                <thead>
                  <tr>
                    <th className={classes.th}>Variant</th>
                    <th className={classes.th}>Sent</th>
                    <th className={classes.th}>Delivered</th>
                    <th className={classes.th}>Opened</th>
                    <th className={classes.th}>Clicked</th>
                    <th className={classes.th}>Opted Out</th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v) => (
                    <tr key={v.variant_label}>
                      <td className={classes.td}>
                        <Chip label={`Variant ${v.variant_label}`} size="small" className={classes.variantChip} />
                      </td>
                      <td className={classes.td}>{num(v.sent_count)}</td>
                      <td className={classes.td}>{pct(v.delivered_pct)}</td>
                      <td className={classes.td}>{pct(v.opened_pct)}</td>
                      <td className={classes.td}>{pct(v.clicked_pct)}</td>
                      <td className={classes.td}>{pct(v.opted_out_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  );
}
